<template>
  <div>
    <b-card>
      <div
        v-if="loadingDevice"
        class="text-center py-4"
      >
        <b-spinner variant="primary" />
      </div>

      <b-tabs v-else-if="device">
        <b-tab
          :title="$t('Details')"
          active
        >
          <div class="dv-head">
            <img
              v-if="iconUrl"
              :src="iconUrl"
              class="dv-icon"
              :alt="typeName"
            >
            <div>
              <div class="dv-title">
                {{ label }}
                <span
                  v-if="isPrimary"
                  class="dv-badge"
                >{{ $t('Main') }}</span>
              </div>
              <div class="dv-subtitle">{{ typeName }}</div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md-6">
              <div class="dv-section-title">{{ $t('Identification') }}</div>
              <ul class="dv-list">
                <li><span>{{ $t('Description') }}</span><strong>{{ device.description || '-' }}</strong></li>
                <li><span>{{ $t('Model') }}</span><strong>{{ (device.device_model && device.device_model.name) || '-' }}</strong></li>
                <li><span>{{ $t('Serial number') }}</span><strong>{{ device.serial_number || '-' }}</strong></li>
                <li><span>{{ $t('IMEI') }}</span><strong>{{ device.imei || '-' }}</strong></li>
                <li><span>{{ $t('MAC Address') }}</span><strong>{{ device.mac_address || '-' }}</strong></li>
                <li><span>Android ID</span><strong>{{ device.android_id || '-' }}</strong></li>
                <li><span>{{ $t('Firmware') }}</span><strong>{{ device.firmware || '-' }}</strong></li>
              </ul>
            </div>

            <div class="col-md-6">
              <div class="dv-section-title">{{ $t('Allocation') }}</div>
              <ul class="dv-list">
                <li><span>{{ $t('Central') }}</span><strong>{{ (device.center && device.center.name) || '-' }}</strong></li>
                <li><span>{{ $t('Unit') }}</span><strong>{{ (device.unit && device.unit.name) || '-' }}</strong></li>
                <li><span>{{ $t('Supplier') }}</span><strong>{{ (device.supplier && device.supplier.name) || '-' }}</strong></li>
                <li><span>{{ $t('Chip') }}</span><strong>{{ chipLabel }}</strong></li>
                <li><span>{{ $t('Acquisition date') }}</span><strong>{{ device.acquisition_date_f || '-' }}</strong></li>
              </ul>

              <div class="dv-section-title mt-3">{{ $t('Current vehicle') }}</div>
              <div
                v-if="currentVehicle"
                class="dv-vehicle"
              >
                <strong>{{ currentVehicle.name }}</strong>
                <span
                  v-if="currentVehicle.license_plate"
                  class="dv-plate"
                >{{ currentVehicle.license_plate }}</span>
              </div>
              <div
                v-else
                class="dv-unlinked"
              >
                <i class="fa fa-unlink" /> {{ $t('Not linked to any vehicle') }}
              </div>

              <div class="dv-section-title mt-3">{{ $t('Last position') }}</div>
              <ul
                v-if="lastUpdate"
                class="dv-list"
              >
                <li><span>{{ $t('Position') }}</span><strong>{{ lastUpdate.lat }}, {{ lastUpdate.long }}</strong></li>
                <li><span>{{ $t('Speed') }}</span><strong>{{ lastUpdate.speed || 0 }} Km/h</strong></li>
                <li><span>{{ $t('Battery level') }}</span><strong>{{ lastUpdate.device_battery_level || '-' }}</strong></li>
                <li><span>{{ $t('Sent date') }}</span><strong>{{ formatDate(lastUpdate.last_tracking_at) }}</strong></li>
              </ul>
              <div
                v-else
                class="dv-unlinked"
              >
                {{ $t('No position') }}
              </div>
            </div>
          </div>
        </b-tab>

        <b-tab :title="$t('Position History')">
          <div
            v-if="assignments.length || gaps.length"
            class="dv-periods"
          >
            <div class="dv-section-title">{{ $t('In the period') }}</div>
            <div
              v-for="assignment in assignments"
              :key="'a-' + assignment.id"
              class="dv-period"
            >
              <strong>{{ (assignment.vehicle && assignment.vehicle.name) || $t('Vehicle') }}</strong>
              <span
                v-if="assignment.vehicle && assignment.vehicle.license_plate"
                class="dv-plate"
              >{{ assignment.vehicle.license_plate }}</span>
              <span
                v-if="assignment.is_primary"
                class="dv-badge"
              >{{ $t('Main') }}</span>
              <span class="dv-range">
                {{ formatDate(assignment.started_at) }} &rarr;
                {{ assignment.ended_at ? formatDate(assignment.ended_at) : $t('now') }}
              </span>
            </div>
            <div
              v-for="(gap, index) in gaps"
              :key="'g-' + index"
              class="dv-period is-gap"
            >
              <strong>{{ $t('Not linked to any vehicle') }}</strong>
              <span class="dv-range">
                {{ formatDate(gap.started_at) }} &rarr; {{ formatDate(gap.ended_at) }}
              </span>
            </div>
          </div>

          <VehicleTrackingMap
            ref="historyMap"
            :url="iconUrl"
            :zoom="13"
            :routeCoordinates="positions"
            :carregando="loadingHistory"
            :vehicleDatalhe="trackingDetail"
            :animate-as-vehicle="false"
            @reload="loadHistory"
            @limpar="clearHistory"
          />
        </b-tab>
      </b-tabs>

      <div
        v-else
        class="dv-empty"
      >
        <p>{{ $t('Device not found') }}</p>
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import VehicleTrackingMap from '../../layouts/Mapa/VehicleTrackingMap.vue'

export default {
  name: 'DeviceView',

  components: { VehicleTrackingMap },

  data() {
    return {
      loadingDevice: false,
      loadingHistory: false,
      device: null,
      positions: [],
      assignments: [],
      gaps: [],
    }
  },

  computed: {
    deviceId() {
      return this.$route.params.id
    },

    label() {
      if (!this.device) {
        return ''
      }

      return this.device.description
        || this.device.imei
        || this.device.serial_number
        || `#${this.device.id}`
    },

    typeName() {
      return (this.device && this.device.device_type && this.device.device_type.name)
        || this.$t('Uncategorized')
    },

    iconUrl() {
      return (this.device && this.device.device_type && this.device.device_type.file) || null
    },

    lastUpdate() {
      return (this.device && this.device.last_update) || null
    },

    activeAssignment() {
      return (this.device && this.device.active_assignment) || null
    },

    isPrimary() {
      return Boolean(this.activeAssignment && this.activeAssignment.is_primary)
    },

    currentVehicle() {
      const assignment = this.activeAssignment

      if (!assignment || !assignment.vehicle_assignment) {
        return null
      }

      return assignment.vehicle_assignment.vehicle || null
    },

    chipLabel() {
      const chip = this.device && this.device.chip

      if (!chip) {
        return '-'
      }

      return chip.number || chip.name || chip.iccid || '-'
    },

    trackingDetail() {
      return { name: this.label, vehicle_image_url: this.iconUrl }
    },
  },

  created() {
    this.loadDevice()
  },

  methods: {
    async loadDevice() {
      this.loadingDevice = true
      try {
        const relations = [
          'deviceType.uploadFiles',
          'deviceModel',
          'supplier',
          'chip',
          'center',
          'unit',
          'lastUpdate',
          'activeAssignment.vehicleAssignment.vehicle',
        ].map(relation => `with[]=${relation}`).join('&')

        const response = await axios.get(
          `${this.$store.state.api}devices/${this.deviceId}?${relations}`,
        )

        const data = (response.data && response.data.data) || []
        this.device = Array.isArray(data) ? data[0] || null : data
      } catch (error) {
        this.$error(error)
      } finally {
        this.loadingDevice = false
      }
    },

    async loadHistory(filter) {
      this.loadingHistory = true
      try {
        const response = await axios.post(
          `${this.$store.state.api}vehicles/findHistoric`,
          {
            device_id: this.deviceId,
            dataFrist: filter.dataFrist,
            dataFim: filter.dataFim,
            limit: filter.limit,
          },
        )

        this.positions = (response.data && response.data.result) || []
        await this.loadAssignments(filter.dataFrist, filter.dataFim)
        this.$nextTick(() => this.$refs.historyMap.fitRouteWhenReady())
      } catch (error) {
        this.$error(error)
      } finally {
        this.loadingHistory = false
      }
    },

    async loadAssignments(start, end) {
      if (!start || !end) {
        this.assignments = []
        this.gaps = []
        return
      }

      try {
        const response = await axios.get(
          `${this.$store.state.api}devices/${this.deviceId}/assignments`,
          { params: { start, end } },
        )

        this.assignments = (response.data && response.data.data) || []
        this.gaps = (response.data && response.data.gaps) || []
      } catch (error) {
        this.assignments = []
        this.gaps = []
        window.console.warn('DeviceView: falha ao carregar vinculos do periodo', error)
      }
    },

    clearHistory() {
      this.positions = []
      this.assignments = []
      this.gaps = []
    },

    formatDate(valor) {
      if (!valor) {
        return '-'
      }

      return new Date(String(valor).replace(' ', 'T')).toLocaleString('pt-BR')
    },
  },
}
</script>

<style scoped>
.dv-head {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #ebe9f1;
}

.dv-icon {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.dv-title {
  font-weight: 600;
  font-size: 1.05rem;
  color: #5e5873;
}

.dv-subtitle {
  font-size: 0.85rem;
  color: #6e6b7b;
}

.dv-section-title {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7367f0;
  margin-bottom: 0.4rem;
}

.dv-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dv-list li {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.28rem 0;
  font-size: 0.85rem;
  color: #6e6b7b;
  border-bottom: 1px solid #f6f6f9;
}

.dv-list strong {
  color: #5e5873;
  text-align: right;
  word-break: break-all;
}

.dv-badge {
  background: #7367f0;
  color: #fff;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.65rem;
  margin-left: 6px;
  vertical-align: middle;
}

.dv-plate {
  background: #f1f5f9;
  color: #334155;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.75rem;
  margin-left: 6px;
}

.dv-vehicle {
  font-size: 0.9rem;
  color: #5e5873;
}

.dv-unlinked {
  font-size: 0.85rem;
  color: #ea5455;
}

.dv-periods {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
  margin: 0.85rem 0;
}

.dv-period {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #5e5873;
  padding: 0.2rem 0;
}

.dv-period.is-gap strong {
  color: #ea5455;
  font-weight: 600;
}

.dv-range {
  margin-left: auto;
  font-size: 0.78rem;
  color: #6e6b7b;
}

.dv-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 2rem 1rem;
  color: #6e6b7b;
  text-align: center;
}
</style>
